// Central-bank decision calendar with live countdowns.
// Dates are copied from each bank's OFFICIAL calendar (checked 2 Oct 2026):
//   Fed  federalreserve.gov/monetarypolicy/fomccalendars.htm
//   ECB  ecb.europa.eu/press/calendars/mgcgc
//   BoE  bankofengland.co.uk/monetary-policy/upcoming-mpc-dates
//   BoJ  boj.or.jp/en/mopo/mpmsche_minu
// Each entry is the DECISION day (the last day of a two-day meeting).
// Update this list once a year when the banks publish the next calendar.

import { zoned, countdown } from '../markets/sessions.js';
import { t, lang } from '../i18n/index.js';

export const BANKS = [
    {
        id: 'fed', name: 'Federal Reserve', short: 'FED', country: 'US', tz: 'America/New_York', time: '14:00',
        dates: ['2026-10-28', '2026-12-09', '2027-01-27', '2027-03-17', '2027-04-28', '2027-06-09', '2027-07-28', '2027-09-15', '2027-10-27', '2027-12-08'],
    },
    {
        id: 'ecb', name: 'European Central Bank', short: 'ECB', country: 'EU', tz: 'Europe/Berlin', time: '14:15',
        dates: ['2026-10-29', '2026-12-17', '2027-02-04', '2027-03-18', '2027-04-29', '2027-06-10', '2027-07-22', '2027-09-09', '2027-10-28', '2027-12-16'],
    },
    {
        id: 'boe', name: 'Bank of England', short: 'BOE', country: 'GB', tz: 'Europe/London', time: '12:00',
        dates: ['2026-11-05', '2026-12-17', '2027-02-04', '2027-03-18', '2027-04-29', '2027-06-17', '2027-07-29', '2027-09-16', '2027-11-04', '2027-12-16'],
    },
    {
        // The BoJ has no fixed announcement time (usually around midday Tokyo time).
        id: 'boj', name: 'Bank of Japan', short: 'BOJ', country: 'JP', tz: 'Asia/Tokyo', time: '12:00', approx: true,
        dates: ['2026-10-30', '2026-12-18', '2027-01-22', '2027-03-18', '2027-04-28', '2027-06-11', '2027-07-22', '2027-09-22', '2027-10-29', '2027-12-17'],
    },
];

/** Next decision for each bank (epoch ms), sorted soonest first. */
export function upcomingDecisions(now = Date.now()) {
    return BANKS.map((b) => {
        const [hh, mm] = b.time.split(':').map(Number);
        const next = b.dates
            .map((d) => {
                const [y, m, day] = d.split('-').map(Number);
                return zoned(y, m, day, hh, mm, b.tz);
            })
            .find((t) => t > now - 2 * 3600 * 1000); // keep "just announced" for 2h
        return next ? { ...b, at: next } : null;
    })
        .filter(Boolean)
        .sort((a, b) => a.at - b.at);
}

const loc = lang === 'en' ? 'en-GB' : lang;
const dateFmt = new Intl.DateTimeFormat(loc, { weekday: 'short', day: 'numeric', month: 'short' });
const timeFmt = new Intl.DateTimeFormat(loc, { hour: '2-digit', minute: '2-digit' });

export function describe(decision, now = Date.now()) {
    const d = new Date(decision.at);
    const when = `${dateFmt.format(d)} · ${decision.approx ? t('cal.timeVaries') : t('cal.yourTime', { time: timeFmt.format(d) })}`;
    const left = decision.at - now;
    const status = left <= 0 ? t('cal.today') : t('cal.in', { t: countdown(left) });
    return { when, status };
}
